import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult
} from '@tanstack/react-query';

import { TemplateStudioModel } from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock,
  getCustomHeader
} from './api.utils';

export const useTemplatesStudio = (
  currentPublisherId?: string,
  templateId?: string
) => {
  const axios = useAxios();
  const queryClient = useQueryClient();

  const getTemplates: UseQueryResult<TemplateStudioModel[]> = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.TEMPLATES_STUDIO, currentPublisherId],
    queryFn: async () => {
      const response = await axios.get(
        getApiRouteOrMock(EApiRoutes.TEMPLATES_STUDIO),
        {},
        getCustomHeader(currentPublisherId)
      );

      return response;
    },
    enabled: !!currentPublisherId
  });

  const getTemplate: UseQueryResult<TemplateStudioModel> = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.TEMPLATES_STUDIO, currentPublisherId, templateId],
    queryFn: async () => {
      if (!currentPublisherId) {
        throw new Error('No publisher ID');
      }

      const response = await axios.get(
        `${getApiRouteOrMock(EApiRoutes.TEMPLATES_STUDIO)}/${templateId}`,
        {},
        getCustomHeader(currentPublisherId)
      );

      return response;
    },
    enabled: !!currentPublisherId && !!templateId
  });

  const deleteTemplate = useMutation(
    async (templateId: string) => {
      return await axios.del(
        `${getApiRouteOrMock(EApiRoutes.TEMPLATES_STUDIO)}/${templateId}`,
        {},
        getCustomHeader(currentPublisherId)
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [EQueryKeys.TEMPLATES_STUDIO, currentPublisherId]
        });
      }
    }
  );

  const updateTemplate = useMutation(
    async ({
      form,
      templateId
    }: {
      form: Omit<
        TemplateStudioModel,
        | 'templateId'
        | 'isGeneralTemplate'
        | 'createdAt'
        | 'updatedAt'
        | 'isActive'
        | 'version'
      >;
      templateId: string;
    }) => {
      return axios.put(
        `${getApiRouteOrMock(EApiRoutes.TEMPLATES_STUDIO)}/${templateId}`,
        form,
        getCustomHeader(currentPublisherId)
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [EQueryKeys.TEMPLATES_STUDIO, currentPublisherId]
        });
      }
    }
  );

  const createTemplate = useMutation(
    async (form: Partial<TemplateStudioModel>) => {
      return axios.post(
        getApiRouteOrMock(EApiRoutes.TEMPLATES_STUDIO),
        form,
        getCustomHeader(currentPublisherId)
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [EQueryKeys.TEMPLATES_STUDIO, currentPublisherId]
        });
      }
    }
  );

  const activateTemplate = useMutation(
    async ({ templateId }: { templateId: string }) => {
      return axios.put(
        `${getApiRouteOrMock(EApiRoutes.TEMPLATES_STUDIO)}/${templateId}/activation`,
        {},
        getCustomHeader(currentPublisherId)
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [EQueryKeys.TEMPLATES_STUDIO, currentPublisherId]
        });
      }
    }
  );

  return {
    getTemplates,
    getTemplate,
    deleteTemplate,
    updateTemplate,
    createTemplate,
    activateTemplate
  };
};
